import React, { Component } from "react";
import * as moment from "moment";
import "moment-timezone";

import {
  ResponsiveContainer,
  LineChart,
  AreaChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  Tooltip,
  Label
} from "recharts";

const BASE_API_ENDPOINT = "/battery-datacenter-api/api/v1";

function formatXAxis(tickItem, multiDay) {
  return moment(tickItem)
    .tz(process.env.REACT_APP_PROJECT_TIMEZONE)
    .format(multiDay ? "MM-DD HH:mm" : "HH:mm");
}

// Returns all calendar days (in project timezone) that fall within [startTs, endTs].
function getDaysInTsRange(startTs, endTs) {
  const tz = process.env.REACT_APP_PROJECT_TIMEZONE;
  const days = [];
  const current = moment(startTs).tz(tz).startOf("day");
  const end = moment(endTs).tz(tz).startOf("day");

  while (current.valueOf() <= end.valueOf()) {
    days.push({
      year: current.year(),
      month: current.month() + 1,
      day: current.date()
    });
    current.add(1, "day");
  }
  return days;
}

class CustomTooltip extends Component {
  render() {
    const { active, payload, label, unitSymbol, multiDay } = this.props;
    if (!active || !payload || !payload.length) return null;
    const val = payload[0] && payload[0].value != null ? payload[0].value : "–";
    const ts = moment(parseInt(label))
      .tz(process.env.REACT_APP_PROJECT_TIMEZONE)
      .format(multiDay ? "MM-DD HH:mm" : "HH:mm");
    return (
      <div className="adb-tooltip">
        {ts}
        <br />
        <strong>
          {val} {unitSymbol}
        </strong>
      </div>
    );
  }
}

class RangeChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sensor: null,
      data: null,
      loading: true,
      error: null
    };
  }

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(prevProps) {
    const { sensorName, startTs, endTs } = this.props;
    if (
      prevProps.sensorName !== sensorName ||
      prevProps.startTs !== startTs ||
      prevProps.endTs !== endTs
    ) {
      this.loadData();
    }
  }

  async loadData() {
    const { sensorName, startTs, endTs } = this.props;
    this.setState({ loading: true, error: null, data: null, sensor: null });

    try {
      const sensorRes = await fetch(
        `${BASE_API_ENDPOINT}/sensor/name/${sensorName}`
      );
      if (!sensorRes.ok) {
        this.setState({
          loading: false,
          error: `Sensor info unavailable for ${sensorName}`
        });
        return;
      }
      const sensor = await sensorRes.json();

      // Fetch data for every calendar day that overlaps the range.
      const days = getDaysInTsRange(startTs, endTs);
      const allData = [];

      for (const d of days) {
        const res = await fetch(
          `${BASE_API_ENDPOINT}/reading/day/${d.year}/${d.month}/${d.day}/${sensorName}`
        );
        if (!res.ok) continue;
        const raw = await res.json();

        for (let i = raw.length - 1; i >= 0; i--) {
          const unixts = parseInt(raw[i].timestamp);
          // Keep only points within the requested range.
          if (unixts < startTs || unixts > endTs) continue;
          allData.push({
            unixts,
            value:
              raw[i].value != null
                ? parseFloat(raw[i].value.toFixed(3))
                : null
          });
        }
      }

      // Sort ascending by time.
      allData.sort((a, b) => a.unixts - b.unixts);

      this.setState({ sensor, data: allData, loading: false });
    } catch (e) {
      this.setState({ loading: false, error: String(e) });
    }
  }

  getSettings(sensor) {
    switch (sensor.unit) {
      case "Amper":
        return {
          physicalQuantityHumanReadable: "Amper [A]",
          physicalQuantitySymbol: "A",
          lineColor: "#e84037",
          yAxisMin:
            sensor.lowestBound != null
              ? parseFloat(sensor.lowestBound)
              : "auto",
          yAxisMax:
            sensor.highestBound != null
              ? parseFloat(sensor.highestBound)
              : "dataMax + 1"
        };
      case "Volt":
        return {
          physicalQuantityHumanReadable: "Voltage [V]",
          physicalQuantitySymbol: "V",
          lineColor: "#35ba43",
          yAxisMin:
            sensor.lowestBound != null
              ? parseFloat(sensor.lowestBound)
              : "auto",
          yAxisMax:
            sensor.highestBound != null
              ? parseFloat(sensor.highestBound)
              : "dataMax + 1"
        };
      default:
        return {
          physicalQuantityHumanReadable: sensor.unit,
          physicalQuantitySymbol: sensor.unit,
          lineColor: "#555",
          yAxisMin: "auto",
          yAxisMax: "dataMax + 1"
        };
    }
  }

  render() {
    const { sensorName } = this.props;
    const { sensor, data, loading, error } = this.state;

    if (loading) {
      return (
        <div className="adb-sensor-chart">
          <div
            className="loader"
            style={{ margin: "80px auto", width: 60, height: 60, borderWidth: 8 }}
          />
        </div>
      );
    }

    if (error) {
      return (
        <div className="adb-sensor-chart">
          <h4>{sensorName}</h4>
          <div
            className="no-data-place-holder"
            style={{ lineHeight: "normal", paddingTop: 60 }}
          >
            {error}
          </div>
        </div>
      );
    }

    if (!sensor) return null;

    const {
      physicalQuantityHumanReadable,
      physicalQuantitySymbol,
      lineColor,
      yAxisMin,
      yAxisMax
    } = this.getSettings(sensor);

    const { startTs, endTs } = this.props;
    const tz = process.env.REACT_APP_PROJECT_TIMEZONE;
    const multiDay =
      moment(startTs).tz(tz).format("YYYY-MM-DD") !==
      moment(endTs).tz(tz).format("YYYY-MM-DD");

    const chartData = data && data.length > 0 ? data : null;
    const chartMargin = { top: 0, right: 10, left: 0, bottom: multiDay ? 16 : 0 };

    const xAxis = (
      <XAxis
        scale="auto"
        tickCount={8}
        allowDataOverflow={false}
        interval="preserveEnd"
        dataKey="unixts"
        domain={["minData", "maxData"]}
        tickFormatter={ts => formatXAxis(ts, multiDay)}
        type="number"
        angle={multiDay ? -30 : 0}
        textAnchor={multiDay ? "end" : "middle"}
        height={multiDay ? 50 : 30}
      />
    );

    const yAxis = (
      <YAxis
        scale="linear"
        domain={[yAxisMin, yAxisMax]}
        dataKey="value"
        type="number"
      >
        <Label
          value={physicalQuantityHumanReadable}
          angle={-90}
          position="insideLeft"
          opacity="0.6"
        />
      </YAxis>
    );

    const tooltip = (
      <Tooltip
        isAnimationActive={false}
        content={<CustomTooltip unitSymbol={physicalQuantitySymbol} multiDay={multiDay} />}
      />
    );

    const grid = <CartesianGrid strokeOpacity="0.7" strokeDasharray="3 3" />;
    const gradientId = `rcGrad-${sensorName}`;

    const voltChart = (
      <AreaChart data={chartData} margin={chartMargin}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={lineColor} stopOpacity={0.6} />
            <stop offset="95%" stopColor={lineColor} stopOpacity={0} />
          </linearGradient>
        </defs>
        {xAxis}
        {yAxis}
        {grid}
        {tooltip}
        <Area
          type="monotone"
          dataKey="value"
          stroke={lineColor}
          fillOpacity={1}
          fill={`url(#${gradientId})`}
          connectNulls={false}
          strokeWidth={2}
          isAnimationActive={false}
        />
      </AreaChart>
    );

    const amperChart = (
      <LineChart data={chartData} margin={chartMargin}>
        {xAxis}
        {yAxis}
        {grid}
        <ReferenceLine y={0} stroke="black" />
        {tooltip}
        <Line
          type="monotone"
          connectNulls={false}
          dataKey="value"
          dot={false}
          stroke={lineColor}
          strokeWidth={2}
          isAnimationActive={false}
        />
      </LineChart>
    );

    return (
      <div className="adb-sensor-chart">
        <h4>{sensor.caption || sensorName}</h4>
        {chartData === null ? (
          <div className="no-data-place-holder">NO DATA AVAILABLE</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            {physicalQuantitySymbol === "V" ? voltChart : amperChart}
          </ResponsiveContainer>
        )}
      </div>
    );
  }
}

export default RangeChart;
